import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import type {
  HazardBrowseRow,
  HazardBrowseSection,
  ProductsHazardsBrowseModel,
} from '@buxus/shared'
import { pathForAppState } from './navigation/appPaths'
import {
  trackButtonPressed,
  type ScreenInteractionContext,
} from './platform/analytics/interactionAnalytics'

const LOCATION_TAB = 0
const PRODUCT_TAB = 1

type ProductsHazardsBrowseProps = {
  model: ProductsHazardsBrowseModel
  operatorId: string
  stack: string[]
  urlSlugForOperator: (operatorId: string) => string
  activeAssetId: string
  onNavigate: (assetId: string) => void
  analytics?: ScreenInteractionContext
}

function hazardListTo(
  operatorId: string,
  stack: string[],
  urlSlugForOperator: (operatorId: string) => string,
  activeAssetId: string,
  row: { hazardId: string; productSubType: string | null },
): string {
  const base = pathForAppState(
    operatorId,
    [...stack, 'screen.assetHazard'],
    urlSlugForOperator,
    { assetId: activeAssetId },
  )
  const q = new URLSearchParams()
  if (row.productSubType) q.set('hp', row.productSubType)
  q.set('hi', row.hazardId)
  const qs = q.toString()
  return qs ? `${base}?${qs}` : base
}

function BrowseSections({
  sections,
  operatorId,
  stack,
  urlSlugForOperator,
  activeAssetId,
  onNavigate,
}: {
  sections: HazardBrowseSection[]
  operatorId: string
  stack: string[]
  urlSlugForOperator: (operatorId: string) => string
  activeAssetId: string
  onNavigate: (assetId: string) => void
}) {
  if (!sections.length) {
    return <p className="products-hazards-browse-empty">No hazards listed for this view.</p>
  }
  return (
    <div className="products-hazards-browse-sections">
      {sections.map((section) => (
        <section
          key={section.heading || section.rows[0]?.hazardId || 'section'}
          className="products-hazards-browse-section"
        >
          {section.heading.trim() ? (
            <h2 className="products-hazards-browse-heading">{section.heading}</h2>
          ) : null}
          <div className="child-nav products-hazards-browse-list" role="navigation">
            {section.rows.map((row: HazardBrowseRow) => (
              <Link
                key={row.hazardId}
                to={hazardListTo(operatorId, stack, urlSlugForOperator, activeAssetId, row)}
                className="child-nav-btn"
                onClick={() => onNavigate(activeAssetId)}
              >
                <span>{row.label}</span>
                <span className="child-nav-chevron" aria-hidden>
                  →
                </span>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

export function ProductsHazardsBrowse({
  model,
  operatorId,
  stack,
  urlSlugForOperator,
  activeAssetId,
  onNavigate,
  analytics,
}: ProductsHazardsBrowseProps) {
  const [tabIndex, setTabIndex] = useState(LOCATION_TAB)

  const selectTab = useCallback(
    (next: number) => {
      setTabIndex((prev) => {
        if (prev === next) return prev
        if (analytics) {
          trackButtonPressed({
            ...analytics,
            button: next === LOCATION_TAB ? 'By Location' : 'By Product',
          })
        }
        return next
      })
    },
    [analytics],
  )
  const sections =
    model.isFacility && tabIndex === LOCATION_TAB ? model.byLocation : model.byProduct

  return (
    <div className="products-hazards-browse">
      {model.isFacility ? (
        <div className="products-hazards-browse-tabs" role="tablist" aria-label="Browse hazards">
          <button
            type="button"
            role="tab"
            aria-selected={tabIndex === LOCATION_TAB}
            className={
              tabIndex === LOCATION_TAB
                ? 'products-hazards-browse-tab products-hazards-browse-tab--active'
                : 'products-hazards-browse-tab'
            }
            onClick={() => selectTab(LOCATION_TAB)}
          >
            By Location
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tabIndex === PRODUCT_TAB}
            className={
              tabIndex === PRODUCT_TAB
                ? 'products-hazards-browse-tab products-hazards-browse-tab--active'
                : 'products-hazards-browse-tab'
            }
            onClick={() => selectTab(PRODUCT_TAB)}
          >
            By Product
          </button>
        </div>
      ) : null}
      <BrowseSections
        sections={sections}
        operatorId={operatorId}
        stack={stack}
        urlSlugForOperator={urlSlugForOperator}
        activeAssetId={activeAssetId}
        onNavigate={onNavigate}
      />
    </div>
  )
}
